import { BackendApi } from "../backend-api/backend-api";
import { ApiConfig } from "../backend-api/api-config";
import { ApiRoutes } from "../backend-api/api-routes";
import { TextLinkParams } from "./TextLinkParamsContext";
import { isValidTextLinkId } from "./text-link-id";
import PageModularApi from "../../contentful/page-modular-api";
import { loadServerTranslations } from "../../i18n/messages";
import { buildLogEntityWithoutRequest, formatMessage, Log } from "../log";

export interface TextLinkResponse {
  vinReg: string;
  vinRegEncrypted?: string;
  textLinkParams: Record<string, string>;
  serviceConfig: Record<string, unknown>;
  createdAt: string;
  expiresAt: string;
}

const backendApi = new BackendApi(ApiConfig.buildBackendUrlForNode());

export const resolveTextLink = async (textLinkId: string): Promise<TextLinkResponse | null> => {
  if (!isValidTextLinkId(textLinkId)) {
    Log.info(
      buildLogEntityWithoutRequest(formatMessage("Skipping text link resolution, invalid text link id", textLinkId)),
    );
    return null;
  }
  return backendApi.request<TextLinkResponse>(ApiRoutes.resolveTextLink(textLinkId));
};

export const buildTextLinkParams = (textLinkData: TextLinkResponse | null): TextLinkParams | null => {
  if (!textLinkData) {
    return null;
  }
  const params: TextLinkParams = {};
  if (textLinkData.vinRegEncrypted?.trim()) {
    params.plate_encoded = textLinkData.vinRegEncrypted;
  } else if (textLinkData.vinReg?.trim()) {
    params.vin = textLinkData.vinReg;
  }
  if (textLinkData.textLinkParams) {
    Object.entries(textLinkData.textLinkParams).forEach(([key, value]) => {
      if (value) {
        params[key] = value;
      }
    });
  }
  return params;
};

const TRANSLATION_NAMESPACES = ["common", "page-index", "page-error", "pages-record-check", "page-pricing", "packages"];

export const buildTextLinkPageProps = async (locale: string, slug: string, partnerName: string, textLinkId: string) => {
  const page = (await PageModularApi.getInstance().getPageModularBySlug(locale, slug)) || null;

  let textLinkData: TextLinkResponse | null = null;
  try {
    textLinkData = await resolveTextLink(textLinkId);
  } catch (e) {
    Log.error(buildLogEntityWithoutRequest(formatMessage("Error resolving text link", textLinkId), e));
  }

  return {
    props: {
      locale,
      messages: await loadServerTranslations(locale, TRANSLATION_NAMESPACES),
      page,
      pageExists: Boolean(page),
      partnerName,
      textLinkData,
      textLinkId,
    },
  };
};
